import { skipToken } from '@reduxjs/toolkit/query/react';
import { useState } from 'react';

import { Trans, t } from '@grafana/i18n';
import { isFetchError } from '@grafana/runtime';
import { Alert, Box, Button, Field, Stack, TextArea } from '@grafana/ui';
import { useAppNotification } from 'app/core/copy/appNotification';
import { getMessageFromError } from 'app/core/utils/errors';
import { AnnoKeyMessage } from 'app/features/apiserver/types';
import { CustomDashboardTemplateInteractions } from 'app/features/dashboard-scene/analytics/dashboard-templates/main';
import { type SaveDashboardDrawer } from 'app/features/dashboard-scene/saving/SaveDashboardDrawer';
import { type DashboardChangeInfo } from 'app/features/dashboard-scene/saving/shared';
import { type DashboardScene } from 'app/features/dashboard-scene/scene/DashboardScene';

import {
  useGetDashboardTemplateQuery,
  useReplaceDashboardTemplateMutation,
} from '../api/clients/dashboardtemplates/v1alpha1';

import { captureDashboardPreview } from './captureDashboardPreview';
import { buildDashboardTemplateBody, preserveOuterSpec } from './wire';

const MAX_MESSAGE_LENGTH = 500;

function isConflictError(error: unknown) {
  return isFetchError(error) && error.status === 409;
}

export interface Props {
  dashboard: DashboardScene;
  drawer: SaveDashboardDrawer;
  changeInfo: DashboardChangeInfo;
}

export function SaveDashboardTemplateForm({ dashboard, drawer, changeInfo }: Props) {
  const notifyApp = useAppNotification();
  const [message, setMessage] = useState('');
  const [replaceDashboardTemplate, { isLoading: isMutating, error }] = useReplaceDashboardTemplateMutation();
  const [isCapturing, setIsCapturing] = useState(false);
  // The preview capture (and, on overwrite, the pre-save refetch) run before the
  // mutation starts. Treat that window as "saving" so Save/Cancel stay disabled and
  // a double-click can't start a second save or duplicate the update.
  const isSaving = isMutating || isCapturing;

  const { hasChanges } = changeInfo;
  const { meta } = dashboard.state;
  const dashboardTemplateUid = meta.dashboardTemplateUid;
  const resourceVersion = meta.k8s?.resourceVersion;

  // Outer template spec fields are fetched live from the RTKQ cache keyed on dashboardTemplateUid
  const {
    data: templateResource,
    isLoading: isTemplateLoading,
    refetch,
  } = useGetDashboardTemplateQuery(dashboardTemplateUid ? { name: dashboardTemplateUid } : skipToken);

  const messageTooLong = message.length > MAX_MESSAGE_LENGTH;
  const isVersionConflict = isConflictError(error);
  const canSubmit = hasChanges && !!dashboardTemplateUid && !!templateResource && !isTemplateLoading && !messageTooLong;

  const onSave = async (overwrite: boolean) => {
    if (!dashboardTemplateUid) {
      return;
    }

    let outerSpec = templateResource?.spec;
    let saveResourceVersion = resourceVersion;
    let previewImage: string | undefined;

    // Guard the async pre-mutation work (the overwrite refetch and the multi-second
    // preview capture) as "saving" so Save/Cancel stay disabled and a double-click
    // can't start a second save before the mutation's own loading flag takes over.
    setIsCapturing(true);
    try {
      // On overwrite, refetch first so we PUT with the server's latest resourceVersion
      // (and pick up any outer-spec changes the other writer made).
      if (overwrite) {
        const refreshed = await refetch().unwrap();
        outerSpec = refreshed.spec;
        saveResourceVersion = refreshed.metadata?.resourceVersion;
      }

      // Refresh the preview from the current dashboard; keep the existing one if
      // capture is unavailable so an update never drops a previously good preview.
      if (outerSpec) {
        previewImage = (await captureDashboardPreview()) ?? outerSpec.previewImage;
      }
    } finally {
      setIsCapturing(false);
    }

    if (!outerSpec) {
      return;
    }

    const dashboardSpec = dashboard.serializer.getSaveModel(dashboard);
    const trimmedMessage = message.trim();

    try {
      const response = await replaceDashboardTemplate({
        name: dashboardTemplateUid,
        dashboardTemplate: buildDashboardTemplateBody({
          name: dashboardTemplateUid,
          resourceVersion: saveResourceVersion,
          annotations: trimmedMessage ? { [AnnoKeyMessage]: trimmedMessage } : undefined,
          spec: preserveOuterSpec({ ...outerSpec, previewImage }, dashboardSpec),
        }),
      }).unwrap();

      // Refresh resourceVersion from the server so a subsequent save in the same session
      // does not 409 against the stale version we loaded with.
      const nextResourceVersion = response.metadata?.resourceVersion ?? saveResourceVersion;

      dashboard.setState({
        meta: {
          ...dashboard.state.meta,
          k8s: {
            ...dashboard.state.meta.k8s,
            resourceVersion: nextResourceVersion,
          },
        },
      });

      dashboard.saveCompleted(dashboardSpec, {
        uid: dashboard.state.uid ?? '',
        url: dashboard.state.meta.url ?? '',
        slug: dashboard.state.meta.slug ?? '',
        version: dashboard.state.version ?? 0,
        status: 'success',
      });

      CustomDashboardTemplateInteractions.updated({
        status: 'completed',
        templateUid: dashboardTemplateUid,
        withMessage: !!trimmedMessage,
        overwriteUsed: overwrite,
      });

      notifyApp.success(t('dashboard-scene.save-dashboard-template-form.saved', 'Dashboard template saved'));

      dashboard.closeModal();
      drawer.state.onSaveSuccess?.();
    } catch (err) {
      if (isConflictError(err)) {
        CustomDashboardTemplateInteractions.updated({ status: 'conflict', templateUid: dashboardTemplateUid });
      } else {
        CustomDashboardTemplateInteractions.updated({
          status: 'failed',
          templateUid: dashboardTemplateUid,
          errorStatus: isFetchError(err) ? err.status : undefined,
        });
      }
      throw err;
    }
  };

  return (
    <Stack direction="column" gap={2}>
      <Alert
        severity="info"
        title={t('dashboard-scene.save-dashboard-template-form.info-title', 'You are editing an organization template')}
      >
        <Trans i18nKey="dashboard-scene.save-dashboard-template-form.info-description">
          Saving will update the embedded dashboard of this template. Users who open the template afterwards will see
          the new content.
        </Trans>
      </Alert>

      <Field label={t('dashboard-scene.save-dashboard-template-form.label-message', 'Message')}>
        <TextArea
          aria-label={t('dashboard-scene.save-dashboard-template-form.aria-label-message', 'Save message')}
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          placeholder={t(
            'dashboard-scene.save-dashboard-template-form.placeholder-message',
            'Add a note to describe your changes (optional).'
          )}
          rows={4}
        />
      </Field>

      {messageTooLong && (
        <Alert
          title={t('dashboard-scene.save-dashboard-template-form.message-length-title', 'Message too long')}
          severity="error"
        >
          <p>
            <Trans i18nKey="dashboard-scene.save-dashboard-template-form.message-length-info">
              The message is {{ messageLength: message.length }} characters, which exceeds the maximum length of 500
              characters. Please shorten it before saving.
            </Trans>
          </p>
        </Alert>
      )}

      {isVersionConflict && (
        <Alert
          title={t(
            'dashboard-scene.save-dashboard-template-form.version-mismatch-title',
            'Someone else has updated this template'
          )}
          severity="error"
        >
          <p>
            <Trans i18nKey="dashboard-scene.save-dashboard-template-form.version-mismatch-message">
              Would you still like to save this template?
            </Trans>
          </p>
          <Box paddingTop={2}>
            <Stack alignItems="center">
              <Button variant="secondary" onClick={() => dashboard.closeModal()} fill="outline" disabled={isSaving}>
                <Trans i18nKey="dashboard-scene.save-dashboard-template-form.cancel">Cancel</Trans>
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => onSave(true)}
                disabled={isSaving}
                icon={isSaving ? 'spinner' : undefined}
              >
                <Trans i18nKey="dashboard-scene.save-dashboard-template-form.save-and-overwrite">
                  Save and overwrite
                </Trans>
              </Button>
            </Stack>
          </Box>
        </Alert>
      )}

      {!isVersionConflict && error !== undefined && (
        <Alert
          title={t('dashboard-scene.save-dashboard-template-form.error-title', 'Failed to save template')}
          severity="error"
        >
          <p>{getMessageFromError(error)}</p>
        </Alert>
      )}

      {!isVersionConflict && (
        <Box paddingTop={2}>
          <Stack alignItems="center">
            <Button variant="secondary" onClick={() => dashboard.closeModal()} fill="outline" disabled={isSaving}>
              <Trans i18nKey="dashboard-scene.save-dashboard-template-form.cancel">Cancel</Trans>
            </Button>
            <Button
              type="button"
              onClick={() => onSave(false)}
              disabled={!canSubmit || isSaving}
              icon={isSaving ? 'spinner' : undefined}
            >
              {isSaving ? (
                <Trans i18nKey="dashboard-scene.save-dashboard-template-form.saving">Saving...</Trans>
              ) : (
                <Trans i18nKey="dashboard-scene.save-dashboard-template-form.save">Save</Trans>
              )}
            </Button>
            {!hasChanges && (
              <div>
                <Trans i18nKey="dashboard-scene.save-dashboard-template-form.no-changes">No changes to save</Trans>
              </div>
            )}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
